import { useNavigate, useLocation } from 'react-router-dom';
import { useCallback } from 'react';

/**
 * useCoachNavigation - Provides navigation functions for coach routes
 * This hook manages the relationship between sidebar clicks and route changes
 */
export const useCoachNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Navigate to a specific coach section
   * @param section - The section to navigate to (dashboard, sessions, upload, roster)
   * @param subItem - Optional sub-item ID for dashboard dashboards or session items
   */
  const navigateToSection = useCallback(
    (section: string, subItem?: string, sessionId?: string) => {
      switch (section) {
        case 'dashboard':
          if (subItem === 'create-new') {
            navigate('/coach?mode=builder');
          } else if (subItem === 'draft-new') {
            navigate('/coach?mode=builder&resumeDraft=1');
          } else if (subItem?.startsWith('draft-edit:')) {
            const editId = subItem.slice('draft-edit:'.length);
            navigate(`/coach?mode=builder&edit=${encodeURIComponent(editId)}&resumeDraft=1`);
          } else if (subItem) {
            // Navigate to custom dashboard using query parameters (like DashboardSelector)
            const sessionParam = sessionId ? `&session=${sessionId}` : '';
            navigate(`/coach?mode=custom&dashboard=${subItem}${sessionParam}`);
          } else {
            // Navigate to default dashboard with optional session
            const sessionParam = sessionId ? `?session=${sessionId}` : '';
            navigate(`/coach${sessionParam}`, { replace: !sessionParam });
          }
          break;
        case 'sessions':
          if (subItem) {
            navigate(`/coach/sessions/${subItem}`);
          } else {
            navigate('/coach/sessions');
          }
          break;
        case 'upload':
          navigate('/coach/upload');
          break;
        case 'roster':
          navigate('/coach/roster');
          break;
        case 'teams':
          navigate('/coach/teams');
          break;
        case 'prototypes':
          navigate('/coach/prototypes');
          break;
        case 'notifications':
          navigate('/coach/notifications');
          break;
        default:
          navigate('/coach');
      }
    },
    [navigate]
  );

  /**
   * Get the current active section based on URL
   * @returns The active section identifier
   */
  const getActiveSection = useCallback((): string => {
    const path = location.pathname;
    if (path.includes('/coach/prototypes')) return 'prototypes';
    if (path.includes('/coach/sessions')) return 'sessions';
    if (path.includes('/coach/upload')) return 'upload';
    if (path.match(/\/coach\/rower\/[^/]+\/(journey|profile)/)) return 'roster';
    if (path.includes('/coach/roster')) return 'roster';
    if (path.includes('/coach/teams')) return 'teams';
    if (path.includes('/coach/notifications')) return 'notifications';
    if (path.includes('/coach/profile')) return 'profile';
    if (path.includes('/coach')) return 'dashboard';
    return 'dashboard';
  }, [location.pathname]);

  /**
   * Get the current active sub-item based on URL
   * @returns The active sub-item ID or null
   */
  const getActiveSubItem = useCallback((): string | null => {
    const path = location.pathname;
    const match = path.match(/\/coach\/(?:dashboard|sessions)\/([^/]+)/);
    return match ? match[1] : null;
  }, [location.pathname]);

  return {
    navigateToSection,
    getActiveSection,
    getActiveSubItem,
  };
};
